<template>
  <div class="portal-page learning-page">
    <div class="portal-shell learning-shell">
      <section class="glass-card learning-hero">
        <div class="learning-hero__head">
          <el-button class="soft-button back-button" circle @click="router.push('/dashboard')">
            <el-icon><ArrowLeft /></el-icon>
          </el-button>
          <div>
            <div class="hero-title-row">
              <div class="hero-icon">
                <el-icon><Opportunity /></el-icon>
              </div>
              <div>
                <h1>自成长智能体</h1>
                <p>AI 智能体通过学习您的操作和决策，持续沉淀经验并优化后续创作建议</p>
                <span class="record-time" v-if="summary">最近更新：{{ formatUpdatedAt(summary.last_updated) }}</span>
              </div>
            </div>
          </div>
          <el-button class="soft-button refresh-button" @click="loadSummary" :loading="loading">刷新学习结果</el-button>
        </div>
      </section>

      <section class="stats-grid">
        <article v-for="item in stats" :key="item.label" class="panel-card stat-card">
          <div>
            <span class="stat-label">{{ item.label }}</span>
            <strong class="stat-value">{{ item.value }}</strong>
          </div>
          <div class="stat-icon" :style="{ color: item.color, background: item.background }">
            <el-icon><component :is="resolveIcon(item.icon)" /></el-icon>
          </div>
        </article>
      </section>

      <section class="panel-card tabs-panel">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          class="tab-button"
          :class="{ 'tab-button--active': activeTab === tab.value }"
          @click="activeTab = tab.value"
        >
          {{ tab.label }}
        </button>
      </section>

      <section v-if="loading" class="panel-card empty-card">
        <h2>正在分析近期用户操作...</h2>
        <p>自成长智能体会结合页面行为、接口反馈和工作流动作，生成最新学习结果。</p>
      </section>

      <section v-else-if="activeTab === 'records'" class="records-list">
        <article v-for="record in records" :key="record.id" class="panel-card record-card">
          <div class="record-card__header">
            <div>
              <div class="record-title-row">
                <h2>{{ record.stage }}</h2>
                <span class="status-pill" :class="record.status_class">{{ record.status }}</span>
              </div>
              <p>{{ record.project }}</p>
              <span class="record-time">{{ record.time }}</span>
            </div>
            <span class="record-module">{{ record.module }}</span>
          </div>

          <div class="record-grid">
            <div class="record-box record-box--blue">
              <span>AI 建议</span>
              <p>{{ record.ai_suggestion }}</p>
            </div>
            <div class="record-box record-box--purple">
              <span>用户反馈</span>
              <p>{{ record.user_feedback }}</p>
            </div>
          </div>

          <div class="record-box record-box--green record-box--full">
            <span>模型改进</span>
            <p>{{ record.model_improvement }}</p>
          </div>
        </article>

        <article v-if="!records.length" class="panel-card empty-card">
          <h2>还没有可展示的学习记录</h2>
          <p>登录后继续创建项目、审批方案或推进工作流，自成长智能体会自动形成记录。</p>
        </article>
      </section>

      <section v-else-if="activeTab === 'insights'" class="insights-grid">
        <article v-for="insight in insights" :key="insight.title" class="panel-card insight-card">
          <div class="insight-icon" :style="{ background: insight.background, color: insight.color }">
            <el-icon><component :is="resolveIcon(insight.icon)" /></el-icon>
          </div>
          <h2>{{ insight.title }}</h2>
          <p>{{ insight.description }}</p>
          <ul>
            <li v-for="item in insight.points" :key="item">{{ item }}</li>
          </ul>
        </article>

        <article v-if="!insights.length" class="panel-card empty-card">
          <h2>洞察生成中</h2>
          <p>当系统积累足够的真实行为样本后，这里会展示用户路径、修订瓶颈和稳定性洞察。</p>
        </article>
      </section>

      <section v-else class="improvements-grid">
        <article v-for="improvement in improvements" :key="improvement.title" class="panel-card improvement-card">
          <div class="improvement-head">
            <div>
              <h2>{{ improvement.title }}</h2>
              <span v-if="improvement.agent_name" class="improvement-agent">{{ improvement.agent_name }}</span>
            </div>
            <span>{{ improvement.version }}</span>
          </div>
          <p>{{ improvement.description }}</p>
          <div class="pill-list">
            <span v-for="tag in improvement.tags" :key="tag" class="pill">{{ tag }}</span>
          </div>
        </article>

        <article v-if="!improvements.length" class="panel-card empty-card">
          <h2>暂无改进建议</h2>
          <p>当前系统还在积累学习样本，后续会根据真实操作生成针对各智能体的升级建议。</p>
        </article>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import {
  ArrowLeft,
  Opportunity,
  Clock,
  CircleCheck,
  MagicStick,
  TrendCharts,
  DataAnalysis,
  User,
  Setting,
  Monitor,
} from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { learningApi, type LearningSummaryResponse } from '@/api/learning'

const router = useRouter()

const activeTab = ref<'records' | 'insights' | 'models'>('records')
const loading = ref(false)
const summary = ref<LearningSummaryResponse | null>(null)

const iconMap = {
  ArrowLeft,
  Opportunity,
  Clock,
  CircleCheck,
  MagicStick,
  TrendCharts,
  DataAnalysis,
  User,
  Setting,
  Monitor,
} as const

const tabs = [
  { label: '学习记录', value: 'records' },
  { label: '洞察总结', value: 'insights' },
  { label: '模型改进', value: 'models' },
] as const

const stats = computed(() => summary.value?.stats || [])
const records = computed(() => summary.value?.records || [])
const insights = computed(() => summary.value?.insights || [])
const improvements = computed(() => summary.value?.improvements || [])

function resolveIcon(iconName?: string | null) {
  if (!iconName) {
    return TrendCharts
  }
  return iconMap[iconName as keyof typeof iconMap] || TrendCharts
}

function formatUpdatedAt(value?: string) {
  if (!value) {
    return '-'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString('zh-CN', { hour12: false })
}

async function loadSummary() {
  loading.value = true
  try {
    summary.value = await learningApi.getSummary()
  } catch (error) {
    ElMessage.error('加载学习中心数据失败')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void loadSummary()
})
</script>

<style scoped>
.learning-shell {
  display: grid;
  gap: 22px;
}

.learning-hero {
  padding: 28px 32px;
}

.learning-hero__head {
  display: flex;
  gap: 18px;
  align-items: center;
}

.hero-title-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-icon,
.stat-icon,
.insight-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 54px;
  height: 54px;
  border-radius: 18px;
  font-size: 24px;
}

.learning-hero h1,
.record-title-row h2,
.insight-card h2,
.improvement-head h2 {
  margin: 0;
}

.learning-hero p,
.record-card__header p,
.insight-card p,
.improvement-card p {
  margin: 6px 0 0;
  color: var(--text-main);
}

.back-button {
  flex: 0 0 auto;
}

.refresh-button {
  flex: 0 0 auto;
  margin-left: auto;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
}

.stat-card {
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  display: block;
  font-size: 14px;
  color: var(--text-soft);
  margin-bottom: 12px;
}

.stat-value {
  font-size: 42px;
  line-height: 1;
}

.tabs-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  padding: 10px;
}

.tab-button {
  border: none;
  background: transparent;
  border-radius: 16px;
  padding: 14px 20px;
  font-weight: 700;
  color: var(--text-main);
  cursor: pointer;
}

.tab-button--active {
  background: linear-gradient(90deg, rgba(138, 63, 252, 0.12), rgba(53, 109, 255, 0.08));
  color: #6d2cff;
}

.records-list,
.insights-grid,
.improvements-grid {
  display: grid;
  gap: 18px;
}

.record-card {
  padding: 26px;
}

.record-card__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.record-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.record-time {
  display: inline-block;
  margin-top: 10px;
  color: var(--text-soft);
  font-size: 13px;
}

.record-module {
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(53, 109, 255, 0.08);
  color: #356dff;
  font-size: 13px;
  font-weight: 700;
}

.record-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;
}

.record-box {
  border-radius: 20px;
  padding: 18px 20px;
}

.record-box span {
  display: block;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
}

.record-box p {
  margin: 0;
  line-height: 1.7;
}

.record-box--blue {
  background: rgba(53, 109, 255, 0.08);
}

.record-box--purple {
  background: rgba(138, 63, 252, 0.08);
}

.record-box--green {
  background: rgba(31, 190, 107, 0.08);
  border: 1px solid rgba(31, 190, 107, 0.18);
}

.record-box--full {
  width: 100%;
}

.insights-grid,
.improvements-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.insight-card,
.improvement-card {
  padding: 24px;
}

.insight-card ul {
  margin: 18px 0 0;
  padding-left: 18px;
  color: var(--text-main);
  line-height: 1.8;
}

.improvement-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
}

.improvement-head span {
  font-size: 13px;
  font-weight: 700;
  color: #6d2cff;
}

.improvement-agent {
  display: inline-block;
  margin-top: 6px;
  color: var(--text-soft);
  font-size: 13px;
}

.empty-card {
  padding: 28px;
}

.empty-card h2 {
  margin: 0 0 12px;
}

.empty-card p {
  margin: 0;
  line-height: 1.7;
  color: var(--text-main);
}

.pill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.pill {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(53, 109, 255, 0.08);
  color: #356dff;
  font-size: 13px;
  font-weight: 700;
}

@media (max-width: 1080px) {
  .stats-grid,
  .insights-grid,
  .improvements-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .learning-hero,
  .record-card,
  .insight-card,
  .improvement-card {
    padding: 20px;
  }

  .learning-hero__head,
  .hero-title-row,
  .record-card__header,
  .record-title-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .stats-grid,
  .record-grid,
  .insights-grid,
  .improvements-grid {
    grid-template-columns: 1fr;
  }

  .tabs-panel {
    grid-template-columns: 1fr;
  }
}
</style>